import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import fetchAPI from '../../common/fetchAPI';
import fetchTimeliveAPI from '../../common/fetchTimeliveAPI';
import { getBaseUrl } from '../../common/getBaseUrl';
import Web from '../../assets/image/Web.svg';
import Mobile from '../../assets/image/Mobile.svg';
import ChromeExtension from '../../assets/image/ChromeExtension.svg';
import { getGlobalAccountPreferenceDataAsync } from '../../common/GlobalSlice';

declare const chrome: any;

const getPreferenceValue = (obj: any, targetKeys: string[]): boolean => {
  if (!obj || typeof obj !== 'object') return false;
  const lowerTargetKeys = targetKeys.map((k) => k.toLowerCase());

  const searchInObj = (currentObj: any): any => {
    if (!currentObj || typeof currentObj !== 'object') return undefined;
    for (const key of Object.keys(currentObj)) {
      if (lowerTargetKeys.includes(key.toLowerCase())) {
        return currentObj[key];
      }
      if (typeof currentObj[key] === 'object' && currentObj[key] !== null) {
        const found = searchInObj(currentObj[key]);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  };

  const result = searchInObj(obj);
  return result === true || result === 'true' || result === 1 || result === '1';
};

export const ExtensionRecent: React.FC = () => {
  const dispatch = useAppDispatch();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const globalState = useAppSelector((state: any) => state.globalState || state);

  const accountid: number = Number(globalState?.sessionAccountId);
  const accountEmployeeId: number = Number(globalState?.sessionAccountEmployeeId);

  const [activeAccountId, setAccountId] = useState<number | string | null>(accountid);
  const [activeEmpId, setActiveEmpId] = useState<number | string | null>(accountEmployeeId);

  const redClient = getPreferenceValue(globalState, ['showClientInTimesheet', 'showClientInTimeSheet', 'showClient', 'isClientVisible']);
  const redCost = getPreferenceValue(globalState, ['showCostCenterInTimesheet', 'showCostCenterInTimeSheet', 'showCostCenter', 'isCostCenterVisible']);
  const redWork = getPreferenceValue(globalState, ['showWorkTypeInTimesheet', 'showWorkTypeInTimeSheet', 'showWorkType', 'isWorkTypeVisible']);

  const [isClientVisible, setIsClientVisible] = useState<boolean>(redClient);
  const [isCostCenterVisible, setIsCostCenterVisible] = useState<boolean>(redCost);
  const [isWorkTypeVisible, setIsWorkTypeVisible] = useState<boolean>(redWork);

  useEffect(() => {
    if (typeof chrome !== 'undefined' && chrome.storage) {
      chrome.storage.local.get(
        [
          'showClientInTimesheet',
          'showClientInTimeSheet',
          'showClient',
          'isClientVisible',
          'showCostCenterInTimesheet',
          'showCostCenterInTimeSheet',
          'showCostCenter',
          'isCostCenterVisible',
          'showWorkTypeInTimesheet',
          'showWorkTypeInTimeSheet',
          'showWorkType',
          'isWorkTypeVisible',
          'accountId',
          'sessionAccountId',
          'accountEmployeeId',
          'sessionAccountEmployeeId'
        ],
        (result: any) => {
          const resolvedAccId = result?.accountId || result?.sessionAccountId;
          const resolvedEmpId = result?.accountEmployeeId || result?.sessionAccountEmployeeId;

          if (!accountid && resolvedAccId) setAccountId(resolvedAccId);
          if (!accountEmployeeId && resolvedEmpId) setActiveEmpId(resolvedEmpId);

          if (result) {
            const storageClient = getPreferenceValue(result, ['showClientInTimesheet', 'showClientInTimeSheet', 'showClient', 'isClientVisible']);
            const storageCost = getPreferenceValue(result, ['showCostCenterInTimesheet', 'showCostCenterInTimeSheet', 'showCostCenter', 'isCostCenterVisible']);
            const storageWork = getPreferenceValue(result, ['showWorkTypeInTimesheet', 'showWorkTypeInTimeSheet', 'showWorkType', 'isWorkTypeVisible']);

            if (storageClient) setIsClientVisible(true);
            if (storageCost) setIsCostCenterVisible(true);
            if (storageWork) setIsWorkTypeVisible(true);
          }
        }
      );
    }
  }, []);

  useEffect(() => {
    if (redClient) setIsClientVisible(true);
    if (redCost) setIsCostCenterVisible(true);
    if (redWork) setIsWorkTypeVisible(true);
  }, [redClient, redCost, redWork]);

  useEffect(() => {
    if (accountid) setAccountId(accountid);
    if (accountEmployeeId) setActiveEmpId(accountEmployeeId);
  }, [accountid, accountEmployeeId]);

  const formatHours = (decimalHours: number) => {
    if (!decimalHours || isNaN(decimalHours)) return '0h 00m';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return `${hours}h ${minutes < 10 ? '0' : ''}${minutes}m`;
  };

  useEffect(() => {
    const fetchRecentData = async () => {
      dispatch(getGlobalAccountPreferenceDataAsync());
      if (!activeEmpId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let currentDate = new Date().toISOString().split('T')[0];

        const periodRes = await fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${currentDate}`, 'GET', '', true);
        if (periodRes.status !== 200) {
          setLoading(false);
          return;
        }
        const periodJson = await periodRes.json();
        const periodData = periodJson?.AccountEmployeeTimeEntryPeriod;
        if (!periodData) {
          setLoading(false);
          return;
        }

        const startDate = periodData.TimeEntryStartDate;
        const endDate = periodData.TimeEntryEndDate;

        const entriesRes = await fetchTimeliveAPI(
          `api/Timesheet/GetRecentTimeEntriesByPeriodAsync?accountId=${activeAccountId}&accountEmployeeId=${activeEmpId}&fromDate=${startDate}&toDate=${endDate}`,
          'GET',
          '',
          true
        );

        if (entriesRes.status !== 200) {
          setLoading(false);
          return;
        }

        const rawData = await entriesRes.json();
        const dataList = rawData.Data || rawData.data || rawData;
        let parsedEntries: any[] = [];

        if (Array.isArray(dataList)) {
          dataList.forEach((row: any) => {
            const isTimeOff = row.IsTimeOff === '1' || row.IsTimeOff === 1 || row.IsTimeOff === true;
            if (isTimeOff) return;

            const hours = row.Hours || row.hours || 0;
            if (hours > 0) {
              const rawDateStr = row.EntryDate || row.entryDate || '';
              const formattedDate = rawDateStr ? rawDateStr.split('T')[0] : '';

              parsedEntries.push({
                id: row.AccountEmployeeTimeEntryId || row.id,
                projectName: row.ProjectName,
                taskName: row.TaskName,
                clientName: row.PartyName,
                costCenter: row.CostCenter || row.costCenter || '',
                workType: row.WorkType || row.workType || '',
                hours: hours,
                formattedHours: formatHours(hours),
                date: formattedDate,
                description: row.Description || '',
                source: row.SystemPlatformSourceName || 'Extension',
              });
            }
          });
        }

        parsedEntries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setEntries(parsedEntries);
      } catch (err) {
        console.error('Error fetching recent entries:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentData();
  }, [activeEmpId]);

  const handleViewAllClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    try {
      const baseUrl = await getBaseUrl();
      if (baseUrl) {
        const formattedBaseUrl = baseUrl.replace(/\/+$/, '');
        const targetUrl = `${formattedBaseUrl}/timesheet`;
        window.open(targetUrl, '_blank');
      } else {
        window.open('/timesheet', '_blank');
      }
    } catch (err) {
      console.error('Error getting dynamic base URL:', err);
      window.open('/timesheet', '_blank');
    }
  };

  return (
    <div className="w-100">
      <h6 className="fw-bolder text-gray-900 mb-3 fs-7">Recent Time Entries</h6>

      <div className="d-flex flex-column gap-3 mb-4" style={{ maxHeight: '450px', overflowY: 'auto' }}>
        {loading ? (
          <div className="text-center text-gray-500 fs-8 py-3">Loading entries...</div>
        ) : entries.length === 0 ? (
          <div className="text-center text-gray-500 fs-8 py-3">No recent entries found</div>
        ) : (
          entries.map((item, index) => {
            const sourceLower = item.source?.toLowerCase() || '';

            const isMobile = sourceLower.includes('mobile');
            const isExtension = sourceLower.includes('extension') || sourceLower.includes('chrome');
            const isWeb = sourceLower.includes('web') && !sourceLower.includes('extension');
            const isManual = sourceLower.includes('manual');

            let badgeClass = 'badge-light-primary text-primary';
            let iconSrc = ChromeExtension;
            let imageSize = { width: '24px', height: '24px' };
            let badgeStyle = {
              fontSize: '11px',
              padding: '5px 10px',
              borderRadius: '50px'
            };
            let dotColor = '#378ADD';

            if (isMobile) {
              badgeClass = 'badge-light-info text-info';
              dotColor = '#378ADD';
              iconSrc = Mobile;
              imageSize = { width: '24px', height: '24px' };
            } else if (isExtension) {
              badgeClass = 'badge-light-success text-success';
              dotColor = '#41bd47';
              iconSrc = ChromeExtension;
              imageSize = { width: '28px', height: '28px' };
              badgeStyle = {
                fontSize: '12px',
                padding: '6px 12px',
                borderRadius: '50px'
              };
            } else if (isManual) {
              badgeClass = 'badge-light-warning text-warning';
              dotColor = '#EF9F27';
              iconSrc = Web;
              imageSize = { width: '24px', height: '24px' };
            } else if (isWeb) {
              badgeClass = 'badge-light-primary text-primary';
              dotColor = '#378ADD';
              iconSrc = Web;
              imageSize = { width: '24px', height: '24px' };
            }

            return (
              <div key={item.id || index} className="card card-bordered shadow-xs p-3 hover-elevation-1 cursor-pointer">
                <div className="d-flex align-items-start gap-3">
                  <div className="d-flex align-items-center justify-content-center mt-1 flex-shrink-0" style={{ width: '35px', height: '40px' }}>
                    <img src={iconSrc} alt="" style={{ ...imageSize, objectFit: 'contain' }} />
                  </div>

                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    
                    {/* 1. CLIENT NAME */}
                    {isClientVisible && item.clientName && (
                      <div 
                        className="fw-bold fs-7 text-gray-700 d-flex align-items-center mb-1.5" 
                        style={{ minWidth: 0 }}
                        title={item.clientName.length > 25 ? "Client : " + item.clientName : "Client"}
                      >
                        <i className="bi bi-building me-3 text-gray-900 fs-8 flex-shrink-0"></i>
                        <span className="text-truncate" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.clientName}
                        </span>
                      </div>
                    )}

                    {/* 2. PROJECT NAME */}
                    <div 
                      className="fw-bold fs-7 text-gray-700 d-flex align-items-center mb-1.5" 
                      style={{ minWidth: 0 }}
                      title={item.projectName.length > 25 ? "Project : " + item.projectName : "Project"}
                    >
                      <i className="bi bi-folder me-3 text-gray-900 fs-8 flex-shrink-0"></i>
                      <span className="text-truncate" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.projectName}
                      </span>
                    </div>

                    {/* 3. TASK NAME */}
                    {item.taskName && (
                      <div 
                        className="fw-bold fs-7 text-gray-700 d-flex align-items-center mb-1.5" 
                        style={{ minWidth: 0 }}
                        title={item.taskName.length > 25 ? "Task : " + item.taskName : "Task"}
                      >
                        <i className="bi bi-card-checklist me-3 text-gray-900 fs-8 flex-shrink-0"></i>
                        <span className="text-truncate" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.taskName}
                        </span>
                      </div>
                    )}

                    {/* 4. WORK TYPE */}
                    {isWorkTypeVisible && item.workType && (
                      <div 
                        className="fw-bold fs-7 text-gray-700 d-flex align-items-center mb-1.5" 
                        style={{ minWidth: 0 }}
                        title={item.workType.length > 25 ? "Work Type : " + item.workType : "Work Type"}
                      >
                        <i className="bi bi-briefcase me-3 text-gray-900 fs-8 flex-shrink-0"></i>
                        <span className="text-truncate" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.workType}
                        </span>
                      </div>
                    )}

                    {/* 5. COST CENTER */}
                    {isCostCenterVisible && item.costCenter && (
                      <div 
                        className="fw-bold fs-7 text-gray-700 d-flex align-items-center mb-1.5" 
                        style={{ minWidth: 0 }}
                        title={item.costCenter.length > 25 ? "Cost Center : " + item.costCenter : "Cost Center"}
                      >
                        <i className="bi bi-diagram-3 me-3 text-gray-900 fs-8 flex-shrink-0"></i>
                        <span className="text-truncate" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.costCenter}
                        </span>
                      </div>
                    )}

                    {/* Footer: Date, Hours & Source Badge */}
                    <div className="d-flex justify-content-between align-items-center pt-2 mt-2 border-top border-gray-300">
                      <span className="fs-8 text-gray-700 fw-medium">
                        <i className="bi bi-clock fs-8 me-1"></i>
                        {item.date} · <strong className="text-dark">{item.formattedHours}</strong>
                      </span>
                      <span
                        className={`badge rounded-pill ${badgeClass}`}
                        style={{
                          ...badgeStyle,
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        {item.source && item.source.trim() !== '-' && (
                          <span
                            style={{
                              width: '6px',
                              height: '6px',
                              minWidth: '6px',
                              minHeight: '6px',
                              backgroundColor: dotColor,
                              borderRadius: '50%',
                              display: 'inline-block',
                              marginRight: '5px',
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {item.source || '-'}
                      </span>
                    </div>

                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="text-center pt-2">
        <a
          href="#timesheet"
          onClick={handleViewAllClick}
          className="btn btn-link btn-color-primary p-0 fs-8 fw-bold text-decoration-none"
        >
          View All Entries <i className="bi bi-chevron-right fs-9 ms-1"></i>
        </a>
      </div>
    </div>
  );
};

export default ExtensionRecent;

// import React, { useState, useEffect } from 'react';
// import { useAppDispatch, useAppSelector } from '../../app/hooks';
// import fetchAPI from '../../common/fetchAPI';
// import fetchTimeliveAPI from '../../common/fetchTimeliveAPI';
// import { getBaseUrl } from '../../common/getBaseUrl';
// import Web from '../../assets/image/Web.svg';
// import Mobile from '../../assets/image/Mobile.svg';
// import ChromeExtension from '../../assets/image/ChromeExtension.svg';
// import { getGlobalAccountPreferenceDataAsync } from '../../common/GlobalSlice';

// declare const chrome: any;

// const getPreferenceValue = (obj: any, targetKeys: string[]): boolean => {
//   if (!obj || typeof obj !== 'object') return false;

//   const lowerTargetKeys = targetKeys.map((k) => k.toLowerCase());

//   const searchInObj = (currentObj: any): any => {
//     if (!currentObj || typeof currentObj !== 'object') return undefined;

//     for (const key of Object.keys(currentObj)) {
//       if (lowerTargetKeys.includes(key.toLowerCase())) {
//         return currentObj[key];
//       }
//       if (typeof currentObj[key] === 'object' && currentObj[key] !== null) {
//         const found = searchInObj(currentObj[key]);
//         if (found !== undefined) return found;
//       }
//     }
//     return undefined;
//   };

//   const result = searchInObj(obj);
//   return result === true || result === 'true' || result === 1 || result === '1';
// };

// export const ExtensionRecent: React.FC = () => {
//   const dispatch = useAppDispatch();
//   const [entries, setEntries] = useState<any[]>([]);
//   const [loading, setLoading] = useState<boolean>(true);

//   const globalState = useAppSelector((state: any) => state.globalState || state);

//   const accountid: number = Number(globalState?.sessionAccountId);
//   const accountEmployeeId: number = Number(globalState?.sessionAccountEmployeeId);

//   const [activeAccountId, setAccountId] = useState<number | string | null>(accountid);
//   const [activeEmpId, setActiveEmpId] = useState<number | string | null>(accountEmployeeId);

//   const isClientVisible = getPreferenceValue(globalState, [
//     'showClientInTimesheet',
//     'showClientInTimeSheet',
//     'showClient',
//     'isClientVisible'
//   ]);

//   const isCostCenterVisible = getPreferenceValue(globalState, [
//     'showCostCenterInTimesheet',
//     'showCostCenterInTimeSheet',
//     'showCostCenter',
//     'isCostCenterVisible'
//   ]);

//   const isWorkTypeVisible = getPreferenceValue(globalState, [
//     'showWorkTypeInTimesheet',
//     'showWorkTypeInTimeSheet',
//     'showWorkType',
//     'isWorkTypeVisible'
//   ]);

//   useEffect(() => {
//     if (accountid) setAccountId(accountid);
//     if (accountEmployeeId) setActiveEmpId(accountEmployeeId);

//     if ((!accountid || !accountEmployeeId) && typeof chrome !== 'undefined' && chrome.storage) {
//       chrome.storage.local.get(
//         ['accountId', 'sessionAccountId', 'accountEmployeeId', 'sessionAccountEmployeeId'],
//         (result: any) => {
//           const resolvedAccId = result?.accountId || result?.sessionAccountId;
//           const resolvedEmpId = result?.accountEmployeeId || result?.sessionAccountEmployeeId;

//           if (!accountid && resolvedAccId) setAccountId(resolvedAccId);
//           if (!accountEmployeeId && resolvedEmpId) setActiveEmpId(resolvedEmpId);
//         }
//       );
//     }
//   }, [accountid, accountEmployeeId]);

//   const formatHours = (decimalHours: number) => {
//     if (!decimalHours || isNaN(decimalHours)) return '0h 00m';
//     const hours = Math.floor(decimalHours);
//     const minutes = Math.round((decimalHours - hours) * 60);
//     return `${hours}h ${minutes < 10 ? '0' : ''}${minutes}m`;
//   };

//   useEffect(() => {
//     const fetchRecentData = async () => {
//       dispatch(getGlobalAccountPreferenceDataAsync());
//       if (!activeEmpId) {
//         setLoading(false);
//         return;
//       }

//       try {
//         setLoading(true);
//         let currentDate = new Date().toISOString().split('T')[0];

//         const periodRes = await fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${currentDate}`, 'GET', '', true);
//         if (periodRes.status !== 200) {
//           setLoading(false);
//           return;
//         }
//         const periodJson = await periodRes.json();
//         const periodData = periodJson?.AccountEmployeeTimeEntryPeriod;
//         if (!periodData) {
//           setLoading(false);
//           return;
//         }

//         const startDate = periodData.TimeEntryStartDate;
//         const endDate = periodData.TimeEntryEndDate;

//         const entriesRes = await fetchTimeliveAPI(
//           `api/Timesheet/GetRecentTimeEntriesByPeriodAsync?accountId=${activeAccountId}&accountEmployeeId=${activeEmpId}&fromDate=${startDate}&toDate=${endDate}`,
//           'GET',
//           '',
//           true
//         );

//         if (entriesRes.status !== 200) {
//           setLoading(false);
//           return;
//         }

//         const rawData = await entriesRes.json();
//         const dataList = rawData.Data || rawData.data || rawData;
//         let parsedEntries: any[] = [];

//         if (Array.isArray(dataList)) {
//           dataList.forEach((row: any) => {
//             const isTimeOff = row.IsTimeOff === '1' || row.IsTimeOff === 1 || row.IsTimeOff === true;
//             if (isTimeOff) return;

//             const hours = row.Hours || row.hours || 0;
//             if (hours > 0) {
//               const rawDateStr = row.EntryDate || row.entryDate || '';
//               const formattedDate = rawDateStr ? rawDateStr.split('T')[0] : '';

//               parsedEntries.push({
//                 id: row.AccountEmployeeTimeEntryId || row.id,
//                 projectName: row.ProjectName,
//                 taskName: row.TaskName,
//                 clientName: row.PartyName,
//                 costCenter: row.CostCenter || row.costCenter || '',
//                 workType: row.WorkType || row.workType || '',
//                 hours: hours,
//                 formattedHours: formatHours(hours),
//                 date: formattedDate,
//                 description: row.Description || '',
//                 source: row.SystemPlatformSourceName || 'Extension',
//               });
//             }
//           });
//         }

//         parsedEntries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
//         setEntries(parsedEntries);
//       } catch (err) {
//         console.error('Error fetching recent entries:', err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchRecentData();
//   }, [activeEmpId]);

//   const handleViewAllClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
//     e.preventDefault();
//     try {
//       const baseUrl = await getBaseUrl();
//       if (baseUrl) {
//         const formattedBaseUrl = baseUrl.replace(/\/+$/, '');
//         const targetUrl = `${formattedBaseUrl}/timesheet`;
//         window.open(targetUrl, '_blank');
//       } else {
//         window.open('/timesheet', '_blank');
//       }
//     } catch (err) {
//       console.error('Error getting dynamic base URL:', err);
//       window.open('/timesheet', '_blank');
//     }
//   };

//   return (
//     <div className="w-100">
//       <h6 className="fw-bolder text-gray-900 mb-3 fs-7">Recent Time Entries</h6>

//       <div className="d-flex flex-column gap-3 mb-4" style={{ maxHeight: '450px', overflowY: 'auto' }}>
//         {loading ? (
//           <div className="text-center text-gray-500 fs-8 py-3">Loading entries...</div>
//         ) : entries.length === 0 ? (
//           <div className="text-center text-gray-500 fs-8 py-3">No recent entries found</div>
//         ) : (
//           entries.map((item, index) => {
//             const sourceLower = item.source?.toLowerCase() || '';

//             const isMobile = sourceLower.includes('mobile');
//             const isExtension =
//               sourceLower.includes('extension') || sourceLower.includes('chrome');
//             const isWeb = sourceLower.includes('web') && !sourceLower.includes('extension');
//             const isManual = sourceLower.includes('manual');

//             let badgeClass = 'badge-light-primary text-primary';
//             let iconSrc = ChromeExtension;
//             let imageSize = { width: '24px', height: '24px' };
//             let badgeStyle = {
//               fontSize: '11px',
//               padding: '5px 10px',
//               borderRadius: '50px'
//             };
//             let dotColor = '#378ADD';

//             if (isMobile) {
//               badgeClass = 'badge-light-info text-info';
//               dotColor = '#378ADD';
//               iconSrc = Mobile;
//               imageSize = { width: '24px', height: '24px' };
//             } else if (isExtension) {
//               badgeClass = 'badge-light-success text-success';
//               dotColor = '#41bd47';
//               iconSrc = ChromeExtension;
//               imageSize = { width: '28px', height: '28px' };
//               badgeStyle = {
//                 fontSize: '12px',
//                 padding: '6px 12px',
//                 borderRadius: '50px'
//               };
//             } else if (isManual) {
//               badgeClass = 'badge-light-warning text-warning';
//               dotColor = '#EF9F27';
//               iconSrc = Web;
//               imageSize = { width: '24px', height: '24px' };
//             } else if (isWeb) {
//               badgeClass = 'badge-light-primary text-primary';
//               dotColor = '#378ADD';
//               iconSrc = Web;
//               imageSize = { width: '24px', height: '24px' };
//             }

//             return (
//               <div key={item.id || index} className="card card-bordered shadow-xs p-3 hover-elevation-1 cursor-pointer">
//                 <div className="d-flex align-items-start gap-3">
//                   <div className="d-flex align-items-center justify-content-center mt-1 flex-shrink-0" style={{ width: '35px', height: '40px' }}>
//                     <img src={iconSrc} alt="" style={{ ...imageSize, objectFit: 'contain' }} />
//                   </div>

//                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
  
//   {isClientVisible && item.clientName && (
//     <div 
//       className="fw-bold fs-7 text-gray-700 d-flex align-items-center mb-1.5" 
//       style={{ minWidth: 0 }}
//       title={item.clientName.length > 25 ?  "Client : " + item.clientName : "Client"}
//     >
//       <i className="bi bi-building me-2 text-gray-900 fs-8 flex-shrink-0"></i>
//       <span className="text-truncate" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
//         {item.clientName}
//       </span>
//     </div>
//   )}

//   <div 
//     className="fw-bold fs-7 text-gray-700 d-flex align-items-center mb-1.5" 
//     style={{ minWidth: 0 }}
//     title={item.projectName.length > 25 ? "Project : " + item.projectName : "Project"}
//   >
//     <i className="bi bi-folder me-2 text-gray-900 fs-8 flex-shrink-0"></i>
//     <span className="text-truncate" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
//       {item.projectName}
//     </span>
//   </div>

//   {item.taskName && (
//     <div 
//       className="fw-bold fs-7 text-gray-700 d-flex align-items-center mb-1.5" 
//       style={{ minWidth: 0 }}
//       title={item.taskName.length > 25 ? "Task : " + item.taskName : "Task"}
//     >
//       <i className="bi bi-card-checklist me-2 text-gray-900 fs-8 flex-shrink-0"></i>
//       <span className="text-truncate" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
//         {item.taskName}
//       </span>
//     </div>
//   )}

//   {isWorkTypeVisible && item.workType && (
//     <div 
//       className="fw-bold fs-7 text-gray-700 d-flex align-items-center mb-1.5" 
//       style={{ minWidth: 0 }}
//       title={item.workType.length > 25 ? "Work Type : " + item.workType : "Work Type"}
//     >
//       <i className="bi bi-briefcase me-2 text-gray-900 fs-8 flex-shrink-0"></i>
//       <span className="text-truncate" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
//         {item.workType}
//       </span>
//     </div>
//   )}

//   {isCostCenterVisible && item.costCenter && (
//     <div 
//       className="fw-bold fs-7 text-gray-700 d-flex align-items-center mb-1.5" 
//       style={{ minWidth: 0 }}
//       title={item.costCenter.length > 25 ? "Cost Center : " + item.costCenter : "Cost Center"}
//     >
//       <i className="bi bi-diagram-3 me-2 text-gray-900 fs-8 flex-shrink-0"></i>
//       <span className="text-truncate" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
//         {item.costCenter}
//       </span>
//     </div>
//   )}

//   <div className="d-flex justify-content-between align-items-center pt-2 mt-2 border-top border-gray-300">
//     <span className="fs-8 text-gray-700 fw-medium">
//       <i className="bi bi-clock fs-8 me-1"></i>
//       {item.date} · <strong className="text-dark">{item.formattedHours}</strong>
//     </span>
//     <span
//       className={`badge rounded-pill ${badgeClass}`}
//       style={{
//         ...badgeStyle,
//         display: 'inline-flex',
//         alignItems: 'center',
//       }}
//     >
//       {item.source && item.source.trim() !== '-' && (
//         <span
//           style={{
//             width: '6px',
//             height: '6px',
//             minWidth: '6px',
//             minHeight: '6px',
//             backgroundColor: dotColor,
//             borderRadius: '50%',
//             display: 'inline-block',
//             marginRight: '5px',
//             flexShrink: 0,
//           }}
//         />
//       )}
//       {item.source || '-'}
//     </span>
//   </div>

// </div>
//                 </div>
//               </div>
//             );
//           })
//         )}
//       </div>

//       <div className="text-center pt-2">
//         <a
//           href="#timesheet"
//           onClick={handleViewAllClick}
//           className="btn btn-link btn-color-primary p-0 fs-8 fw-bold text-decoration-none"
//         >
//           View All Entries <i className="bi bi-chevron-right fs-9 ms-1"></i>
//         </a>
//       </div>
//     </div>
//   );
// };

// export default ExtensionRecent;

// import React, { useState, useEffect } from 'react';
// import { useAppDispatch, useAppSelector } from '../../app/hooks';
// import fetchAPI from '../../common/fetchAPI';
// import fetchTimeliveAPI from '../../common/fetchTimeliveAPI';
// import { getBaseUrl } from '../../common/getBaseUrl';
// import Web from '../../assets/image/Web.svg';
// import Mobile from '../../assets/image/Mobile.svg';
// import ChromeExtension from '../../assets/image/ChromeExtension.svg';
// import { getGlobalAccountPreferenceDataAsync } from '../../common/GlobalSlice';

// declare const chrome: any;

// export const ExtensionRecent: React.FC = () => {
//   const dispatch = useAppDispatch();
//   const [entries, setEntries] = useState<any[]>([]);
//   const [loading, setLoading] = useState<boolean>(true);

//   const { sessionAccountEmployeeId, timeZoneId, sessionAccountId,showClientInTimesheet } = useAppSelector((state) => state.globalState);

//   const accountid: number = Number(sessionAccountId);
//   const accountEmployeeId: number = Number(sessionAccountEmployeeId);

//   const [activeAccountId, setAccountId] = useState<number | string | null>(accountid);
//   const [activeEmpId, setActiveEmpId] = useState<number | string | null>(accountEmployeeId);
//   const showClientFlag: boolean = Boolean(showClientInTimesheet);
//   const [isClientVisible, setIsClientVisible] = useState<boolean>(showClientFlag);

//   useEffect(() => {
//     if (accountid) {
//       setAccountId(accountid);
//     }

//     if (accountEmployeeId) {
//       setActiveEmpId(accountEmployeeId);
//     }

//     if ((!accountid || !accountEmployeeId) && typeof chrome !== 'undefined' && chrome.storage) {
//       chrome.storage.local.get(
//         ['accountId', 'sessionAccountId', 'accountEmployeeId', 'sessionAccountEmployeeId'],
//         (result: any) => {
//           const resolvedAccId = result?.accountId || result?.sessionAccountId;
//           const resolvedEmpId = result?.accountEmployeeId || result?.sessionAccountEmployeeId;

//           if (!accountid && resolvedAccId) {
//             setAccountId(resolvedAccId);
//           }
//           if (!accountEmployeeId && resolvedEmpId) {
//             setActiveEmpId(resolvedEmpId);
//           }
//         }
//       );
//     }
//   }, [accountid, accountEmployeeId]);



// useEffect(() => {
//   if (showClientInTimesheet !== undefined) {
//     setIsClientVisible(Boolean(showClientInTimesheet));
//   }
// }, [showClientInTimesheet]);

// useEffect(() => {
//   if (typeof chrome !== 'undefined' && chrome.storage) {
//     chrome.storage.local.get(['showClientInTimesheet'], (result: any) => {
//       if (result?.showClientInTimesheet !== undefined) {
//         setIsClientVisible(Boolean(result.showClientInTimesheet));
//       }
//     });
//   }
// }, []);

//   const formatHours = (decimalHours: number) => {
//     if (!decimalHours || isNaN(decimalHours)) return '0h 00m';
//     const hours = Math.floor(decimalHours);
//     const minutes = Math.round((decimalHours - hours) * 60);
//     return `${hours}h ${minutes < 10 ? '0' : ''}${minutes}m`;
//   };

//   useEffect(() => {
//     const fetchRecentData = async () => {
//       dispatch(getGlobalAccountPreferenceDataAsync());
//       if (!activeEmpId) {
//         setLoading(false);
//         return;
//       }

//       try {
//         setLoading(true);
//         let currentDate = new Date().toISOString().split('T')[0];

//         const periodRes = await fetchAPI(`api/Timesheets/GetTimesheetPeriodForReact/${activeEmpId}/${currentDate}`, 'GET', '', true);
//         if (periodRes.status !== 200) {
//           setLoading(false);
//           return;
//         }
//         const periodJson = await periodRes.json();
//         const periodData = periodJson?.AccountEmployeeTimeEntryPeriod;
//         if (!periodData) {
//           setLoading(false);
//           return;
//         }

//         const startDate = periodData.TimeEntryStartDate;
//         const endDate = periodData.TimeEntryEndDate;

//         const entriesRes = await fetchTimeliveAPI(
//           `api/Timesheet/GetRecentTimeEntriesByPeriodAsync?accountId=${activeAccountId}&accountEmployeeId=${activeEmpId}&fromDate=${startDate}&toDate=${endDate}`,
//           'GET',
//           '',
//           true
//         );
       
//         if (entriesRes.status !== 200) {
//           setLoading(false);
//           return;
//         }

//         const rawData = await entriesRes.json();
//         const dataList = rawData.Data || rawData.data || rawData;
//          console.log('GetRecentTimeEntriesByPeriodAsync :',dataList)
//         let parsedEntries: any[] = [];

//         if (Array.isArray(dataList)) {
//           dataList.forEach((row: any) => {
//             const isTimeOff = row.IsTimeOff === '1' || row.IsTimeOff === 1 || row.IsTimeOff === true;
//             if (isTimeOff) return;

//             const hours = row.Hours || row.hours || 0;
//             if (hours > 0) {
//               const rawDateStr = row.EntryDate || row.entryDate || '';
//               const formattedDate = rawDateStr ? rawDateStr.split('T')[0] : '';

//               parsedEntries.push({
//                 id: row.AccountEmployeeTimeEntryId || row.id,
//                 projectName: row.ProjectName,
//                 taskName: row.TaskName,
//                 clientName: row.PartyName,
//                 hours: hours,
//                 formattedHours: formatHours(hours),
//                 date: formattedDate,
//                 description: row.Description || '',
//                 source: row.SystemPlatformSourceName || 'Extension',
//               });
//             }
//           });
//         }

//         parsedEntries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
//         setEntries(parsedEntries);
//       } catch (err) {
//         console.error('Error fetching recent entries:', err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchRecentData();
//   }, [activeEmpId]);

//  const handleViewAllClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
//   e.preventDefault();
//   try {
//     const baseUrl = await getBaseUrl();
//     if (baseUrl) {
//       const formattedBaseUrl = baseUrl.replace(/\/+$/, '');
//       const targetUrl = `${formattedBaseUrl}/timesheet`;
//       window.open(targetUrl, '_blank');
//     } else {
//       window.open('/timesheet', '_blank');
//     }
//   } catch (err) {
//     console.error('Error getting dynamic base URL:', err);
//     window.open('/timesheet', '_blank');
//   }
// };

//   return (
//     <div className="w-100">
//       <h6 className="fw-bolder text-gray-900 mb-3 fs-7">Recent Time Entries</h6>

//       <div className="d-flex flex-column gap-3 mb-4" style={{ maxHeight: '450px', overflowY: 'auto' }}>
//         {loading ? (
//           <div className="text-center text-gray-500 fs-8 py-3">Loading entries...</div>
//         ) : entries.length === 0 ? (
//           <div className="text-center text-gray-500 fs-8 py-3">No recent entries found</div>
//         ) : (
//           entries.map((item, index) => {
//             const sourceLower = item.source?.toLowerCase() || '';

//             const isMobile = sourceLower.includes('mobile');
//             const isExtension = sourceLower.includes('extension') || sourceLower.includes('chrome');
//             const isWeb = sourceLower.includes('web') && !sourceLower.includes('extension');
//             const isManual = sourceLower.includes('manual');

//             let badgeClass = 'badge-light-primary text-primary';
//             let iconSrc = ChromeExtension;
//             let imageSize = { width: '24px', height: '24px' };
//             let badgeStyle = { fontSize: '11px', padding: '5px 10px', fontWeight: 'bold' };

//             if (isMobile) {
//               badgeClass = 'badge-light-info text-info';
//               iconSrc = Mobile;
//               imageSize = { width: '24px', height: '24px' };
//             } else if (isExtension) {
//               badgeClass = 'badge-light-success text-success';
//               iconSrc = ChromeExtension;
//               imageSize = { width: '28px', height: '28px' };
//               badgeStyle = { fontSize: '12px', padding: '6px 12px', fontWeight: 'bold' };
//             } else if (isManual) {
//               badgeClass = 'badge-light-warning text-warning';
//               iconSrc = Web;
//               imageSize = { width: '24px', height: '24px' };
//             } else if (isWeb) {
//               badgeClass = 'badge-light-primary text-primary';
//               iconSrc = Web;
//               imageSize = { width: '24px', height: '24px' };
//             }

//             return (
//               <div key={item.id || index} className="card card-bordered shadow-xs p-3 hover-elevation-1 cursor-pointer">
//                 <div className="d-flex align-items-start gap-3">
//                   <div className="d-flex align-items-center justify-content-center mt-1 flex-shrink-0" style={{ width: '35px', height: '35px' }}>
//                     <img src={iconSrc} alt="" style={{ ...imageSize, objectFit: 'contain' }} />
//                   </div>

//                   <div className="flex-grow-1 min-w-0">
//                     <div className="d-flex justify-content-between align-items-center mb-1">
//                       <span className="fw-bold text-dark fs-7 text-truncate">{item.projectName}</span>
                      
//                     </div>

//                     <div className="fs-8 text-gray-800 fw-semibold text-truncate">{item.taskName}</div>
//                     {isClientVisible && item.clientName && (
//                       <div className="fs-9 text-gray-700 mb-2 text-truncate">{item.clientName}</div>
//                     )}
//                     {/* <div className="fs-9 text-gray-700 mb-2 text-truncate">{item.clientName}</div> */}

//                     <div className="d-flex justify-content-between align-items-center pt-2 border-top border-gray-200">
//                       <span className="fs-9 text-gray-700 fw-medium">
//                         <i className="bi bi-clock fs-9 me-1"></i>
//                         {item.date} · <strong className="text-dark"> {item.formattedHours}</strong>
//                       </span>
//                       <span className={`badge ${badgeClass}`} style={badgeStyle}>
//                         {item.source}
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             );
//           })
//         )}
//       </div>

//       <div className="text-center pt-2">
//         <a
//           href="#timesheet"
//           onClick={handleViewAllClick}
//           className="btn btn-link btn-color-primary p-0 fs-8 fw-bold text-decoration-none"
//         >
//           View All Entries <i className="bi bi-chevron-right fs-9 ms-1"></i>
//         </a>
//       </div>
//     </div>
//   );
// };

// export default ExtensionRecent;